import React, { FC, useMemo } from "react";
import GenericCrudTableModal from "@/components/tables/genericCrudTableModal";
import { replaceDate } from "@/helper/dateHandling";
import { MRT_ColumnDef } from "material-react-table";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import { api } from "@/helper/api";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { ACCIDENT_BOOK_MODAL_STYLE } from "@/components/accidentBook/accidentBookModalStyle";

interface AccidentBookQuickReportProps {
  open: boolean;
  subdomain: string;
  handleSetError: (error: any) => void;
  handleNotificationMessage: (message: any) => void;
  handleNotificationVariant: (variant: any) => void;
  handleNotification: (value: boolean) => void;
  handleCloseModal: () => void;
}

const AccidentBookQuickReport: FC<AccidentBookQuickReportProps> = ({
  open,
  subdomain,
  handleSetError,
  handleNotification,
  handleNotificationMessage,
  handleNotificationVariant,
  handleCloseModal,
}) => {
  const { t } = useTranslation();
  const selectedChildCompanySubjectId: any = useSelector(
    (state: RootState) => state.parent.subjectId,
  );

  const accidentBookQuickReportColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
        enableHiding: false,
        enableGlobalFilter: false,
      },
      {
        header: t("approved_on"),
        id: "approved_on",
        accessorKey: "approved_on",
        enableSorting: false,
        enableColumnFilter: false,

        accessorFn: (row: any) => {
          if (row.approved_on !== null) {
            return (
              <span>
                <CheckIcon sx={{ color: "#008c7d" }} />
                {replaceDate(row.approved_on)}
              </span>
            );
          } else {
            return (
              <span>
                <RemoveIcon sx={{ color: "#dc3545" }} />
              </span>
            );
          }
        },
      },
      {
        header: t("approved_by"),
        accessorKey: "approved_by",
        customColumnFilterId: "approved_by_name",
        id: "approved_by",
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue()?.name}</span>;
        },
      },
      {
        header: t("first_name"),
        accessorKey: "first_name",
        id: "first_name",
        required: true,
        accessorFn: (row: any) => {
          return row?.first_name;
        },
        enableForCreate: true,
      },
      {
        header: t("last_name"),
        accessorKey: "last_name",
        id: "last_name",
        required: true,
        accessorFn: (row: any) => {
          return row?.last_name;
        },
        enableForCreate: true,
      },
      {
        header: t("phone"),
        accessorKey: "phone",
        customColumnFilterId: "phone",
        id: "phone",
        customFilterParameter: "_icontains",
        enableForCreate: true,
      },
      {
        header: t("department"),
        id: "department",
        accessorKey: "department",
        accessorFn: (row: any) => {
          return row?.department;
        },
        enableForCreate: true,
        // entity: "department",
        required: true,

        // useGlobalStateToImpersonate: subdomain === "admin" ? true : false,
      },
      {
        header: t("activity"),
        accessorKey: "activity",
        id: "activity",
        accessorFn: (row: any) => {
          return row?.activity;
        },
        enableForCreate: true,
        required: true,
      },
      {
        header: t("accident_datetime"),
        accessorKey: "accident_datetime",
        id: "accident_datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
        enableForCreate: true,
        dateTimePicker: true,
      },
      {
        header: t("accident_location"),
        id: "accident_location",
        accessorKey: "accident_location",
        accessorFn: (row: any) => {
          return row?.accident_location;
        },
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("accident_description"),
        id: "accident_description",
        accessorKey: "accident_description",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("accident_results_description"),
        id: "accident_results_description",
        accessorKey: "accident_results_description",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("witness_name"),
        id: "witness_name",
        accessorKey: "witness_name",
        enableForCreate: true,
        required: true,
      },
      {
        header: t("witness_phone"),
        id: "witness_phone",
        accessorKey: "witness_phone",
        enableForCreate: true,
      },
      {
        header: t("measures"),
        id: "measures",
        accessorKey: "measures",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("first_responder_name"),
        id: "first_responder_name",
        accessorKey: "first_responder_name",
        enableForCreate: true,
        required: true,
      },
      {
        header: t("first_responder_phone"),
        id: "first_responder_phone",
        accessorKey: "first_responder_phone",
        enableForCreate: true,
      },
      {
        header: t("first_aid_datetime"),
        id: "first_aid_datetime",
        accessorKey: "first_aid_datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
        enableForCreate: true,
        dateTimePicker: true,
      },
      {
        header: t("accident_insurance_doctor_visited"),
        id: "accident_insurance_doctor_visited",
        accessorKey: "accident_insurance_doctor_visited",
        accessorFn: (row: any) => {
          if (row.accident_insurance_doctor_visited) {
            return t("yes");
          } else return t("no");
        },
        enableForCreate: true,
        checkBox: true,
      },
      {
        header: t("emergency_address"),
        id: "emergency_address",
        accessorKey: "emergency_address",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("additional_information"),
        id: "additional_information",
        accessorKey: "additional_information",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
    ],
    [],
  );

  const handleSubmit = (data: any) => {
    let result = data;

    if (
      subdomain === "manage" &&
      (selectedChildCompanySubjectId !== "" ||
        selectedChildCompanySubjectId !== undefined)
    ) {
      result = { ...data, impersonate_subject: selectedChildCompanySubjectId };
    }

    api.genericApiRequest({
      method: "post",
      entity: "accidentBook",
      submitData: result,
      successHandler: () => {
        handleNotificationMessage(t("save_successful"));
        handleNotificationVariant("success");
        handleNotification(true);
        setTimeout(() => {
          handleNotification(false);
          handleNotificationMessage("");
          handleNotificationVariant(undefined);
        }, 200);
        handleCloseModal();
      },
      failHandler: (error: any) => {
        handleSetError(error);
      },
    });
  };
  return (
    <>
      <GenericCrudTableModal
        columns={accidentBookQuickReportColumns}
        open={open}
        presetValues={[]}
        submitButtonText={t("save")}
        modalMode="insert"
        onSubmit={(values: any) => {
          handleSubmit(values);
        }}
        onClose={handleCloseModal}
        handleError={handleSetError}
        customModalStyle={ACCIDENT_BOOK_MODAL_STYLE}
        useTwoColumnModalLayout
      />
    </>
  );
};

export default AccidentBookQuickReport;
